import React, { useMemo } from 'react'
import {
  FileText,
  ClipboardList,
  Receipt,
  Calculator,
  ShoppingBag,
  Wand2,
  BarChart3,
  EyeOff,
  Eye,
  PenLine,
} from 'lucide-react'
import { Card } from '../ui/Card'
import type { FeaturePermissions, UserType } from '../../types/exchange'
import type { FeatureKey } from '../../lib/permissions'
import { FEATURE_LABELS, DEFAULT_FEATURE_PERMISSIONS } from '../../lib/permissions'
import { roleLabel } from '../../lib/roleFormatting'
import type { ExtendedUserType } from './UserTypeSelector'

export type PermissionDisplayLevel = 'read' | 'read_write' | 'hidden'

export interface DomainPermission {
  feature: FeatureKey
  level: PermissionDisplayLevel
}

const FEATURE_ICONS: Record<FeatureKey, React.ElementType> = {
  documents: FileText,
  forms: ClipboardList,
  irs_forms: Receipt,
  calculator: Calculator,
  marketplace: ShoppingBag,
  wizard: Wand2,
  financials: BarChart3,
}

const LEVEL_OPTIONS: Array<{ value: PermissionDisplayLevel; label: string; icon: React.ElementType }> = [
  { value: 'read', label: 'Read', icon: Eye },
  { value: 'read_write', label: 'Read+Write', icon: PenLine },
  { value: 'hidden', label: 'Hidden', icon: EyeOff },
]

function featurePermissionsToDisplayLevel(
  fp: FeaturePermissions,
  feature: FeatureKey,
): PermissionDisplayLevel {
  const raw = fp[feature] as unknown
  if (!raw || (Array.isArray(raw) && (raw as unknown[]).length === 0)) {
    return 'hidden'
  }
  if (Array.isArray(raw)) {
    const hasWrite = (raw as string[]).some((action) =>
      ['upload', 'sign', 'fill', 'generate', 'use', 'browse', 'view'].includes(action)
    )
    return hasWrite ? 'read_write' : 'read'
  }
  return 'read_write'
}

function displayLevelToActions(feature: FeatureKey, level: PermissionDisplayLevel): string[] {
  if (level === 'hidden') return []
  const writeActionsMap: Record<FeatureKey, string[]> = {
    documents: ['read', 'upload', 'sign'],
    forms: ['read', 'fill'],
    irs_forms: ['read', 'generate'],
    calculator: ['use'],
    marketplace: ['browse'],
    wizard: ['use'],
    financials: ['view'],
  }
  const readActionsMap: Record<FeatureKey, string[]> = {
    documents: ['read'],
    forms: ['read'],
    irs_forms: ['read'],
    calculator: ['use'],
    marketplace: ['browse'],
    wizard: ['use'],
    financials: ['view'],
  }
  return level === 'read_write' ? writeActionsMap[feature] : readActionsMap[feature]
}

export function buildFeaturePermissionsFromLevels(
  levels: Record<FeatureKey, PermissionDisplayLevel>,
): FeaturePermissions {
  const result: FeaturePermissions = {}
  for (const feature of Object.keys(levels) as FeatureKey[]) {
    const actions = displayLevelToActions(feature, levels[feature])
    if (actions.length > 0) {
      result[feature] = actions as any
    }
  }
  return result
}

function defaultLevelsForType(userType: UserType): Record<FeatureKey, PermissionDisplayLevel> {
  const defaults = DEFAULT_FEATURE_PERMISSIONS[userType] ?? {}
  const allFeatures = Object.keys(FEATURE_LABELS) as FeatureKey[]
  const result = {} as Record<FeatureKey, PermissionDisplayLevel>
  for (const feature of allFeatures) {
    result[feature] = featurePermissionsToDisplayLevel(defaults, feature)
  }
  return result
}

interface UserPermissionsCardProps {
  userType: ExtendedUserType
  featurePermissions?: FeaturePermissions
  onChange?: (updated: FeaturePermissions) => void
  editable?: boolean
}

export const UserPermissionsCard: React.FC<UserPermissionsCardProps> = ({
  userType,
  featurePermissions,
  onChange,
  editable = false,
}) => {
  const allFeatures = Object.keys(FEATURE_LABELS) as FeatureKey[]
  const preset = userType !== 'custom'

  const resolvedLevels: Record<FeatureKey, PermissionDisplayLevel> = useMemo(() => {
    if (featurePermissions) {
      const levels = {} as Record<FeatureKey, PermissionDisplayLevel>
      for (const feature of allFeatures) {
        levels[feature] = featurePermissionsToDisplayLevel(featurePermissions, feature)
      }
      return levels
    }
    if (preset) {
      return defaultLevelsForType(userType as UserType)
    }
    const empty = {} as Record<FeatureKey, PermissionDisplayLevel>
    for (const feature of allFeatures) {
      empty[feature] = 'hidden'
    }
    return empty
  }, [featurePermissions, userType, preset])

  const handleLevelChange = (feature: FeatureKey, level: PermissionDisplayLevel) => {
    if (!onChange) return
    const updated = { ...resolvedLevels, [feature]: level }
    onChange(buildFeaturePermissionsFromLevels(updated))
  }

  const canEdit = editable && !preset

  return (
    <Card className="p-4">
      <div className="mb-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Current Permissions</h3>
          {preset && (
            <span className="text-xs text-muted-foreground italic">
              Preset for {roleLabel(userType)}
            </span>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {allFeatures.map((feature) => {
          const Icon = FEATURE_ICONS[feature]
          const currentLevel = resolvedLevels[feature]
          return (
            <Card
              key={feature}
              className="p-3 border border-border bg-muted/30"
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm font-medium text-foreground flex-1">
                  {FEATURE_LABELS[feature]}
                </span>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {LEVEL_OPTIONS.map(({ value, label, icon: LevelIcon }) => {
                  const active = currentLevel === value
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => canEdit && handleLevelChange(feature, value)}
                      aria-pressed={active}
                      disabled={!canEdit}
                      className={[
                        'flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors',
                        active
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground',
                        canEdit && !active ? 'hover:bg-muted/80 cursor-pointer' : '',
                        !canEdit ? 'cursor-default' : '',
                      ].join(' ')}
                    >
                      <LevelIcon className="h-3 w-3" />
                      {label}
                    </button>
                  )
                })}
              </div>
            </Card>
          )
        })}
      </div>
    </Card>
  )
}
